// src/pages/tools/ToolList.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Code as CodeIcon,
  FilterList as FilterIcon,
  CheckCircle as EnabledIcon,
  Cancel as DisabledIcon,
  HelpOutline as HelpIcon,
  MoreVert as MoreIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon,
  Sort as SortIcon,
  SmartToy as SmartToyIcon,
  // Refresh as RefreshIcon // Removed as clearAgentCache is removed
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { toolService } from '../../api/toolService';

// Tool type definitions with colors and icons
const TOOL_TYPES_DISPLAY_INFO = { // Renamed to avoid conflict with TOOL_TYPES in ToolEditor
  search: { label: 'Search', color: 'primary', icon: <SearchIcon fontSize="small" /> },
  api: { label: 'API', color: 'secondary', icon: <CodeIcon fontSize="small" /> },
  calculation: { label: 'Calculation', color: 'success', icon: null }, // Add icons if desired
  database: { label: 'Database', color: 'info', icon: null },
  utility: { label: 'Utility', color: 'warning', icon: null },
  custom: { label: 'Custom', color: 'default', icon: null }
};

const ToolList = () => {
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAgent, setFilterAgent] = useState('all');
  const [sortOrder, setSortOrder] = useState('name_asc');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);

  useEffect(() => {
    loadTools();
  }, []);

  const loadTools = async () => {
    try {
      setLoading(true);
      const response = await toolService.getAllTools();
      if (response.isError) {
        toast.error('Failed to load tools: ' + response.message);
        setTools([]);
      } else {
        setTools(response || []);
      }
    } catch (error) { // Should be caught by handleApiError, but as a fallback
      toast.error('Failed to load tools: ' + (error.response?.data?.detail || error.message));
      console.error(error);
      setTools([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredTools = useMemo(() => {
    let result = Array.isArray(tools) ? [...tools] : [];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(tool => 
        tool.name.toLowerCase().includes(query) || 
        (tool.description && tool.description.toLowerCase().includes(query)) ||
        (tool.id && tool.id.toLowerCase().includes(query)) // tool.id is the slug
      );
    }
    
    if (filterType !== 'all') {
      result = result.filter(tool => tool.tool_type === filterType);
    }
    
    if (filterStatus !== 'all') {
      result = result.filter(tool => 
        (filterStatus === 'enabled' && tool.enabled) || 
        (filterStatus === 'disabled' && !tool.enabled)
      );
    }
    
    if (filterAgent !== 'all') {
      result = result.filter(tool => 
        (filterAgent === 'with_agent' && tool.requires_dedicated_agent) || 
        (filterAgent === 'without_agent' && !tool.requires_dedicated_agent)
      );
    }
    
    switch (sortOrder) {
      case 'name_asc':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name_desc':
        result.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'created_desc':
        result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
      case 'created_asc':
        result.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
        break;
      default:
        break;
    }
    
    return result;
  }, [tools, searchQuery, filterType, filterStatus, filterAgent, sortOrder]);

  const handleToggleStatus = async (toolId, currentStatus) => { // toolId is UUID
    const action = currentStatus ? 'disable' : 'enable';
    const confirmMessage = `Are you sure you want to ${action} this tool?`;
    
    if (window.confirm(confirmMessage)) {
      try {
        const response = currentStatus 
          ? await toolService.disableTool(toolId) 
          : await toolService.enableTool(toolId);
        
        if (response.isError) {
          toast.error(`Failed to ${action} tool: ${response.message}`);
        } else {
          toast.success(`Tool ${action}d successfully`);
          setTools(prevTools => 
            prevTools.map(tool => 
              tool.id === toolId ? { ...tool, enabled: !currentStatus } : tool // Ensure tool.id is UUID
            )
          );
        }
      } catch (error) {
        toast.error(`Failed to ${action} tool: ${error.message}`);
        console.error(`Error ${action}ing tool:`, error);
      }
    }
  };
  
  const handleMenuOpen = (event, tool) => {
    setAnchorEl(event.currentTarget);
    setSelectedTool(tool);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTool(null);
  };

  const handleDeleteTool = async () => {
    if (!selectedTool || !selectedTool.id) { // selectedTool.id is UUID
      handleMenuClose();
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete the tool "${selectedTool.name}" (ID: ${selectedTool.id})? This action cannot be undone.`)) {
      try {
        const response = await toolService.deleteTool(selectedTool.id); // Pass UUID
        if (response.isError){
            toast.error(`Failed to delete tool: ${response.message}`);
        } else {
            toast.success('Tool deleted successfully');
            loadTools(); 
        }
      } catch (error) {
        toast.error(`Failed to delete tool: ${error.message}`);
      }
    }
    handleMenuClose();
  };

  // Removed handleClearAgentCache as the functionality is removed

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, 
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', md: 'center' }, 
        mb: 3,
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" component="h1">
            Tool Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage custom tools for your AI assistant to use
          </Typography>
        </Box>
        
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Tooltip title="View Tool Documentation">
            <Button 
              variant="outlined" 
              onClick={() => navigate('/tools/documentation')} // Ensure this route exists
              startIcon={<HelpIcon />}
              size="medium"
            >
              Documentation
            </Button>
          </Tooltip>
          
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={() => navigate('/tools/new')} // Navigates to ToolEditor for creation
            size="medium"
          >
            Create New Tool
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search tools by name, description or ID (slug)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="medium"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="medium">
              <InputLabel id="tool-type-filter-label">Tool Type</InputLabel>
              <Select
                labelId="tool-type-filter-label"
                value={filterType}
                label="Tool Type"
                onChange={(e) => setFilterType(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">All Types</MenuItem>
                {Object.entries(TOOL_TYPES_DISPLAY_INFO).map(([key, { label }]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="medium">
              <InputLabel id="tool-status-filter-label">Status</InputLabel>
              <Select
                labelId="tool-status-filter-label"
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="enabled">Enabled</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="medium">
              <InputLabel id="tool-agent-filter-label">Agent</InputLabel>
              <Select
                labelId="tool-agent-filter-label"
                value={filterAgent}
                label="Agent"
                onChange={(e) => setFilterAgent(e.target.value)}
              >
                <MenuItem value="all">All Tools</MenuItem>
                <MenuItem value="with_agent">With Dedicated Agent</MenuItem>
                <MenuItem value="without_agent">Without Dedicated Agent</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="medium">
              <InputLabel id="tool-sort-label">Sort By</InputLabel>
              <Select
                labelId="tool-sort-label"
                value={sortOrder}
                label="Sort By"
                onChange={(e) => setSortOrder(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <SortIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="name_asc">Name (A-Z)</MenuItem>
                <MenuItem value="name_desc">Name (Z-A)</MenuItem>
                <MenuItem value="created_desc">Newest First</MenuItem>
                <MenuItem value="created_asc">Oldest First</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {!loading && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredTools.length} of {tools.length} tools
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip 
              size="small" 
              icon={<EnabledIcon fontSize="small" />} 
              label={`${(Array.isArray(tools) ? tools : []).filter(t => t.enabled).length} Enabled`} 
              color="success" 
              variant="outlined"
            />
            <Chip 
              size="small" 
              icon={<DisabledIcon fontSize="small" />} 
              label={`${(Array.isArray(tools) ? tools : []).filter(t => !t.enabled).length} Disabled`} 
              color="default" 
              variant="outlined"
            />
            <Chip 
              size="small" 
              icon={<SmartToyIcon fontSize="small" />} 
              label={`${(Array.isArray(tools) ? tools : []).filter(t => t.requires_dedicated_agent).length} With Agent`} 
              color="secondary" 
              variant="outlined"
            />
          </Box>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredTools.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          py: 8,
          textAlign: 'center'
        }}>
          <SearchIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>No tools found</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Try adjusting your search or filters, or create a new tool.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => navigate('/tools/new')}
          >
            Create New Tool
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredTools.map(tool => ( // tool.id is UUID here from backend ToolResponse
            <Grid item xs={12} sm={6} md={4} key={tool.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip 
                        size="small" 
                        label={TOOL_TYPES_DISPLAY_INFO[tool.tool_type]?.label || tool.tool_type} 
                        color={TOOL_TYPES_DISPLAY_INFO[tool.tool_type]?.color || 'default'}
                        icon={TOOL_TYPES_DISPLAY_INFO[tool.tool_type]?.icon}
                        />
                        {tool.requires_dedicated_agent && (
                          <Tooltip title="Uses dedicated agent">
                            <Chip 
                              size="small" 
                              label="Agent" 
                              color="secondary"
                              icon={<SmartToyIcon fontSize="small" />}
                            />
                          </Tooltip>
                        )}
                      </Box>
                      <Box>
                        <Tooltip title={tool.enabled ? "Enabled - Click to disable" : "Disabled - Click to enable"}>
                          <Switch
                            size="small"
                            checked={tool.enabled}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleToggleStatus(tool.id, tool.enabled); // Pass UUID
                            }}
                            color="success"
                          />
                        </Tooltip>
                      </Box>
                    </Box>
                    
                    <Typography variant="h6" component="h2" gutterBottom noWrap title={tool.name}>
                      {tool.name}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        height: '4.5em', // Approx 3 lines
                        textOverflow: 'ellipsis',
                      }}
                      title={tool.description}
                    >
                      {tool.description || "No description provided"}
                    </Typography>
                    
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }} title={`UUID: ${tool.id}`}>
                      UUID: {tool.id} 
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} title={`Slug: ${tool.slug}`}>
                      Slug: {tool.slug} {/* Assuming slug is part of ToolResponse now */}
                    </Typography>
                    
                    {tool.created_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Created: {new Date(tool.created_at).toLocaleDateString()}
                      </Typography>
                    )}
                  </CardContent>
                  
                  <Divider />
                  
                  <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
                    <Box>
                      <Tooltip title="Edit Tool Details">
                        <IconButton  
                          size="small" 
                          onClick={() => navigate(`/tools/${tool.id}/edit`)} // Navigates to ToolEditor with UUID
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Code">
                        <IconButton 
                          size="small" 
                          onClick={() => navigate(`/tools/${tool.id}/edit?tab=code`)} // Updated
                        >
                          <CodeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Test Tool">
                        <IconButton size="small" onClick={() => navigate(`/tools/${tool.id}/test`)}>
                          <TestIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {tool.requires_dedicated_agent && (
                        <Tooltip title="Configure Agent">
                          <IconButton size="small" onClick={() => navigate(`/tools/${tool.id}/edit?tab=agent`)}> {/* Changed from 2 to 'agent' */}
                            <SmartToyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    
                    <Box>
                      <Tooltip title="More Actions">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, tool);
                          }}
                        >
                          <MoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
        
        <Menu
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()} // Prevent card click when menu is open
        >
          <MenuItem onClick={() => {
            if (selectedTool) navigate(`/tools/${selectedTool.id}/edit`); // Pass UUID
            handleMenuClose();
          }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit Details
          </MenuItem>
          <MenuItem onClick={() => {
            if (selectedTool) navigate(`/tools/${selectedTool.id}/edit?tab=code`); // Updated
            handleMenuClose();
          }}>
            <CodeIcon fontSize="small" sx={{ mr: 1 }} />
            Edit Code
          </MenuItem>
          {selectedTool?.requires_dedicated_agent && (
            <MenuItem onClick={() => {
              if (selectedTool) navigate(`/tools/${selectedTool.id}/edit?tab=agent`); // Updated
              handleMenuClose();
            }}>
              <SmartToyIcon fontSize="small" sx={{ mr: 1 }} />
              Configure Agent
            </MenuItem>
          )}
          <MenuItem onClick={() => {
            if (selectedTool) navigate(`/tools/${selectedTool.id}/test`);
            handleMenuClose();
          }}>
            <TestIcon fontSize="small" sx={{ mr: 1 }} />
            Test Tool
          </MenuItem>
          <Divider />
          {/* Removed Clear Agent Cache menu item */}
          <MenuItem 
            onClick={handleDeleteTool}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete Tool
          </MenuItem>
        </Menu>
      </Box>
    );
  };
  
  export default ToolList;